// Structural fingerprint scoring for adapter identify() (Phase 1a).
//
// Lockstep with evals/adapters/_fingerprint.py — same signals, same weights,
// same thresholds, so a file classifies identically on desktop (pdfplumber)
// and cloud (pdf.js). Scores reward presence + co-occurrence of independent
// structural signals so no single signal is load-bearing:
//   - a repeating record delimiter (Message N of M / Comm ID N) — the backbone,
//     and the signal that survives a missing cover page;
//   - per-record field-label tokens;
//   - vendor footer/boilerplate.
//
// We count line-anchored labels (robust to the pdfplumber⇄pdf.js intra-token
// spacing divergence), never prose.

import { combinedText, normalizeWs, type Sample } from "./_contract";
import { scoreOfwCsvHeaders } from "./ofw";
import { mergeSplitSaidHeaders } from "./two_houses";

// --- OFW structural signals (cover-bearing AND cover-less; both templates) ---
// OFW ships three report layouts (see ofw.ts): the per-message "Message
// Report" (`Message N of M`, `Sent:`/`From:`/`Subject:` labels, a branded
// footer) and, since 2026-10, the conversation-grouped "Messages Report"
// (`Message n of k Conversation c of C`, inline capital `SENT … FROM` /
// `SUBJECT` lines, a bare `Page N of M` footer). Each signal accepts either
// spelling so both score identically, and so does the redesigned ungrouped
// layout (2026-10-28: `Message N of M` over the inline capital lines), which
// needs no signal of its own. Lockstep with _fingerprint.py.
const OFW_RECORD_RE = /^Message \d+ of \d+(?:[ \t]+Conversation \d+ of \d+)?\s*$/gm;
const OFW_SENT_RE = /^(?:Sent:|SENT \d)/m;
// `FROM <name>` alone: pdf.js may break the conversation template's wide
// `SENT … FROM …` row after the datetime.
const OFW_FROM_RE = /^(?:From:|SENT .* FROM |FROM \S)/m;
const OFW_SUBJECT_RE = /^(?:Subject:|SUBJECT(?:\s|$))/m;
// The bare `Page N of M` only counts behind the backbone gate, so an
// arbitrary paginated PDF gains nothing from it.
const OFW_FOOTER_RE = /^(?:\|?\s*Message Report )?Page \d+ of \d+\s*$/m;
const OFW_BANNER_RE = /^Messages? Report\s*$/m;
const OFW_COVER_RE = /^(?:Timezone:|Parents:|Contains:)/m;

// --- Civil Communicator structural signals ---
const CC_RECORD_RE = /^Comm ID \d+\s*$/gm;
const CC_DATETIME_RE = /^Date\/Time\b/m;
const CC_FORWARDED_RE = /^Forwarded\b/m;
const CC_TERMINAL_RE = /^(?:Review Action|Monitoring Override)\b/m;
const CC_BANNER_RE = /^Civil Communicator\s*$/m;
const CC_FOOTER_RE = /communicator\.knack\.com/m;

// --- 2houses conversations structural signals ---
// Repeating per-message delimiter: "<Sender> said: <Weekday> <DD> <Month>
// <YYYY> <HH> h <MM>". The localized " h " time separator + "said:" makes this
// distinctive; it recurs once per message and survives a missing title page.
const TWO_HOUSES_RECORD_RE =
  /^.+ said: [A-Za-z]+ \d{1,2} [A-Za-z]+ \d{4} \d{1,2} h \d{2}\s*$/gm;
const TWO_HOUSES_BANNER_RE = /^2houses conversations\s*$/m;
const TWO_HOUSES_EXPORT_RE = /^Exported on\b/m;
const TWO_HOUSES_SUBJECT_RE = /^Subject : /m;

// --- TalkingParents Complete Message Record structural signals ---
// Repeating per-message delimiter: "<Name> on <M/D/YYYY> at <H:MM:SS AM/PM>
// said:" — recurs once per message and survives a missing cover page. The
// trailing "said:" + the seconds-bearing 12-hour time make it distinctive
// (2houses' said-header has the reversed order and no am/pm). Footer/auth
// signals are line-PREFIX anchored: pd.js splits the cover's two-column
// variant after "TalkingParents", and both extractors keep the interior
// footer lines whole. Lockstep with _fingerprint.py.
const TP_RECORD_RE =
  /^.+ on \d{1,2}\/\d{1,2}\/\d{4} at \d{1,2}:\d{2}:\d{2} [AP]M said:\s*$/gm;
const TP_FOOTER_GEN_RE = /^Record Generated By TalkingParents\b/m;
const TP_AUTH_RE = /^Unique Authentication Code/m;
const TP_CREATED_RE =
  /^Created by: .+ on \d{1,2}\/\d{1,2}\/\d{4} at \d{1,2}:\d{2}:\d{2} [AP]M\s*$/m;
// Full-line receipt shape — NOT a bare substring, so ordinary prose that
// merely mentions "viewed this subject on" can't add confidence to a
// foreign export. Lockstep with _fingerprint.py _TP_VIEWED_RE.
const TP_VIEWED_RE =
  /^.+ viewed this subject on \d{1,2}\/\d{1,2}\/\d{4} at \d{1,2}:\d{2}:\d{2} [AP]M\s*$/m;
const TP_COVER_RE = /^COMPLETE MESSAGE RECORD\s*$/m;

function present(re: RegExp, text: string): boolean {
  return re.test(text);
}

// True when the (global) pattern recurs at least minCount times. A repeating
// record delimiter is the backbone signal; one match could be a quoted
// fragment. Lockstep with _fingerprint.py _repeats.
function repeats(re: RegExp, text: string, minCount = 2): boolean {
  // re is /…/gm with lastIndex state — count via matchAll on a fresh regex.
  const fresh = new RegExp(re.source, re.flags);
  let count = 0;
  for (const _m of text.matchAll(fresh)) {
    count += 1;
    if (count >= minCount) return true;
  }
  return false;
}

export function scoreOfw(sample: Sample): number {
  if (sample.kind !== "pdf") return 0.0;
  const text = combinedText(sample);
  if (!text) return 0.0;

  if (!repeats(OFW_RECORD_RE, text)) {
    // Without the repeating per-message delimiter this isn't OFW.
    return 0.0;
  }

  let score = 0.0;
  score += 0.45; // repeating record delimiter — the backbone
  if (present(OFW_SENT_RE, text)) score += 0.18;
  if (present(OFW_FROM_RE, text)) score += 0.12;
  if (present(OFW_SUBJECT_RE, text)) score += 0.1;
  if (present(OFW_FOOTER_RE, text)) score += 0.1;
  else if (present(OFW_BANNER_RE, text)) score += 0.05;
  if (present(OFW_COVER_RE, text)) score += 0.05; // bonus, never required
  // Rounded to 2 dp before the caller's threshold comparison — see the note
  // above `present`. Lockstep with _fingerprint.py.
  return Math.min(Math.round(score * 100) / 100, 1.0);
}

export function scoreCivilCommunicator(sample: Sample): number {
  if (sample.kind !== "pdf") return 0.0;
  const text = combinedText(sample);
  if (!text) return 0.0;

  if (!repeats(CC_RECORD_RE, text)) return 0.0;

  let score = 0.0;
  score += 0.45; // repeating Comm ID delimiter — the backbone
  if (present(CC_DATETIME_RE, text)) score += 0.15;
  if (present(CC_FORWARDED_RE, text)) score += 0.15;
  if (present(CC_TERMINAL_RE, text)) score += 0.1;
  if (present(CC_BANNER_RE, text)) score += 0.08;
  if (present(CC_FOOTER_RE, text)) score += 0.07;
  // Rounded to 2 dp before the caller's threshold comparison — see the note
  // above `present`. Lockstep with _fingerprint.py.
  return Math.min(Math.round(score * 100) / 100, 1.0);
}

export function scoreTwoHouses(sample: Sample): number {
  // Backbone = a repeating "<Sender> said: <date> HH h MM" message delimiter
  // (the signal that survives a missing title page). The "2houses conversations"
  // banner, the "Exported on …" stamp, and "Subject :" thread headers add
  // confidence. Distinctive format → high bar OK. Returns 0 without the
  // backbone. Lockstep with _fingerprint.py score_two_houses.
  if (sample.kind !== "pdf") return 0.0;
  // Rejoin headers the browser's pdf.js extractor splits across two lines (see
  // mergeSplitSaidHeaders) so detection survives the same real exports the
  // parser now handles. No-op on the desktop/pdfplumber shape.
  const text = mergeSplitSaidHeaders(combinedText(sample));
  if (!text) return 0.0;

  if (!repeats(TWO_HOUSES_RECORD_RE, text)) return 0.0;

  let score = 0.45; // repeating message delimiter — the backbone
  if (present(TWO_HOUSES_BANNER_RE, text)) score += 0.25;
  if (present(TWO_HOUSES_EXPORT_RE, text)) score += 0.1;
  if (present(TWO_HOUSES_SUBJECT_RE, text)) score += 0.1;
  // Rounded to 2 dp before the caller's threshold comparison — see the note
  // above `present`. Lockstep with _fingerprint.py.
  return Math.min(Math.round(score * 100) / 100, 1.0);
}

export function scoreTalkingParents(sample: Sample): number {
  // Backbone = the repeating "<Name> on <date> at <time> said:" message
  // delimiter (the signal that survives a missing cover page). The running
  // footer ("Record Generated By TalkingParents" + the Unique Authentication
  // Code), the "Created by:" subject headers, the "viewed this subject"
  // receipts, and the cover banner add confidence. Distinctive certified
  // format → high bar OK. Returns 0 without the backbone. Lockstep with
  // _fingerprint.py score_talking_parents.
  if (sample.kind !== "pdf") return 0.0;
  const text = combinedText(sample);
  if (!text) return 0.0;

  if (!repeats(TP_RECORD_RE, text)) return 0.0;

  let score = 0.45; // repeating message delimiter — the backbone
  if (present(TP_FOOTER_GEN_RE, text)) score += 0.15;
  if (present(TP_AUTH_RE, text)) score += 0.1;
  if (present(TP_CREATED_RE, text)) score += 0.1;
  if (present(TP_VIEWED_RE, text)) score += 0.1;
  if (present(TP_COVER_RE, text)) score += 0.05;
  // Rounded to 2 dp before the caller's threshold comparison — see the note
  // above `present`. Lockstep with _fingerprint.py.
  return Math.min(Math.round(score * 100) / 100, 1.0);
}

// --- AppClose Records Export structural signals ---
// NOT line-anchored, unlike every scorer above, and that is deliberate:
// AppClose record headers WRAP mid-header ("… (viewed by Alex Rivera on June
// 28," / "2024 at 5:55 PM):"), so a `^…$` pattern matches almost nothing on a
// real export. Matched against whitespace-NORMALIZED text, which also makes
// these immune to the pdfplumber⇄pdf.js spacing divergence (pdf.js renders
// " : " where pdfplumber gives ":", and puts a PUA glyph inside the
// cross-zone back-references).
const AC_DATE = String.raw`[A-Z][a-z]+ \d{1,2}, \d{4}`;
const AC_TIME = String.raw`\d{1,2}:\d{2} [AP]M`;
const AC_RECORD_RE = new RegExp(
  String.raw` on ${AC_DATE} at ${AC_TIME} (?:texted|sent attachment) ` +
    String.raw`\(viewed by .+? on ${AC_DATE} at ${AC_TIME}\)\s*:`,
  "g",
);
// The running header, on every page — the vendor string. It is the closest
// thing this export has to a certification anchor, because it ships with no
// certification block, no record ID and no signature.
const AC_BANNER_RE = /AppClose Records Export/;
const AC_PERIOD_RE = new RegExp(
  String.raw`Period\s*:\s*${AC_DATE} at ${AC_TIME} to ${AC_DATE}`,
);
const AC_ATTACH_REF_RE = /\(See attached\s+\S{0,2}\s*on page \d+\)/;
const AC_ORIG_REF_RE = /\(See original message\s+\S{0,2}\s*on page \d+\)/;

export function scoreAppclose(sample: Sample): number {
  // Backbone = the repeating record header with its parenthesized read
  // receipt. The "AppClose Records Export" running header, the "Period: …"
  // range, and the cross-zone back-references add confidence. Returns 0
  // without the backbone. Lockstep with _fingerprint.py score_appclose.
  if (sample.kind !== "pdf") return 0.0;
  const text = normalizeWs(combinedText(sample));
  if (!text) return 0.0;

  if (!repeats(AC_RECORD_RE, text)) return 0.0;

  let score = 0.45; // repeating record header + read receipt — the backbone
  if (present(AC_BANNER_RE, text)) score += 0.25;
  if (present(AC_PERIOD_RE, text)) score += 0.1;
  if (present(AC_ATTACH_REF_RE, text)) score += 0.1;
  if (present(AC_ORIG_REF_RE, text)) score += 0.1;
  // Rounded before the caller's threshold comparison: backbone + banner +
  // period is exactly 0.80 in decimal and 0.7999999999999999 in IEEE-754, so
  // an ordinary export with no attachments and no replies would miss its own
  // auto threshold by one ulp and route to confirm.
  return Math.min(Math.round(score * 100) / 100, 1.0);
}

// Canonical column set the generic-CSV adapter requires (mirrors
// generic_csv REQUIRED_COLUMNS and _fingerprint.py _GENERIC_CSV_REQUIRED).
const GENERIC_CSV_REQUIRED = [
  "timestamp",
  "sender",
  "recipient",
  "body",
  "attachments",
] as const;

// OFW message CSV (Date,Time,Sender,Message) — a KNOWN, dedicated shape
// (Phase 3, Piece A). Delegates to the OFW adapter's header scorer so the
// signature lives in one place. Lockstep with _fingerprint.py score_ofw_csv.
export function scoreOfwCsv(sample: Sample): number {
  if (sample.kind !== "csv") return 0.0;
  return scoreOfwCsvHeaders(sample.csvHeaders);
}

export function scoreGenericCsv(sample: Sample): number {
  if (sample.kind !== "csv") return 0.0;
  const headers = new Set(sample.csvHeaders.map((h) => h.toLowerCase()));
  if (headers.size === 0) return 0.0;
  const present_ = GENERIC_CSV_REQUIRED.filter((c) => headers.has(c)).length;
  if (present_ === 0) return 0.0;
  if (present_ === GENERIC_CSV_REQUIRED.length) return 1.0;
  // Partial header overlap — recognizable but not the canonical schema; below
  // the auto threshold so it routes to confirm/column-mapping (Phase 3).
  return 0.3 + 0.4 * (present_ / GENERIC_CSV_REQUIRED.length);
}
